import { useState, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGoBack } from '../../hooks/useGoBack';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  BookOpen,
  AlertTriangle,
  ExternalLink,
  StickyNote,
  ImageIcon,
  Eye,
  RefreshCw,
} from 'lucide-react';
import {
  useProforma,
  useUpdateProforma,
  useBatchUpsertItems,
  useDeleteProformaItem,
  useProformaImpact,
  useProformaFotos,
  useCreateProformaFoto,
  useSyncRecipePrices,
  useSyncMedidasToProforma,
  type ProformaItemDTO,
  type ProformaImpactDTO,
} from '../../hooks/useProformas';
import { FileUpload, type UploadedFile } from '../../components/shared/FileUpload';
import { FotoThumbnail } from '../../components/shared/FotoThumbnail';
import { CreateRecipeModal } from '../../components/shared/CreateRecipeModal';
import { useRecipes, type RecipeListDTO } from '../../hooks/useRecipes';
import { useMedidas, type MedidaDTO } from '../../hooks/useMedidas';
import { useMedidaFotos } from '../../hooks/useMedidaFotos';
import { useMedidaSets } from '../../hooks/useMedidaSets';
import { useProject } from '../../hooks/useProjects';
import { useAuthStore } from '../../stores/auth-store';
import { generateItemCode } from '../../lib/item-utils';
import { calcPorcentajeFromPrecioUnitario } from '../../lib/proforma-calc';
import { useAplicaciones } from '../../hooks/useAplicaciones';
import { useClientRecipeRates } from '../../hooks/useClientRecipeRates';
import { updateProformaSchema } from '@shared/validation/proforma-schemas';
import { rubroTipoEnum, unidadEnum } from '@shared/validation/enums';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge, StatusBadge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { SkeletonTable } from '../../components/ui/Skeleton';
import { toast } from '../../components/ui/Toast';

// ── Item form schema (client-side) ──
const itemFormSchema = z.object({
  _serverId: z.string().optional(),
  recipeId: z.string().optional().nullable(),
  codigo: z.string().min(1, 'Requerido'),
  descripcion: z.string().min(1, 'Requerido'),
  descripcionProyecto: z.string().optional().nullable(),
  categoria: rubroTipoEnum,
  unidad: unidadEnum,
  cantidad: z.number().min(0),
  precioMo: z.number().min(0),
  precioMateriales: z.number().min(0),
  porcentajeUtilidad: z.number().min(0).max(100),
  aplicacionId: z.string().uuid().optional().nullable(),
  orden: z.number().int(),
});

const formSchema = z.object({
  items: z.array(itemFormSchema),
});

type ItemFormValues = z.infer<typeof itemFormSchema>;
type FormValues = z.infer<typeof formSchema>;

interface ProformaTotals {
  subtotal_mano_obra: number;
  subtotal_materiales: number;
  subtotal_sin_utilidad: number;
  utilidad_valor: number;
  utilidad_porcentaje: number;
  subtotal_con_utilidad: number;
  descuento_amount: number;
  subtotal_con_descuento: number;
  iva: number;
  total: number;
}

const UNIDAD_OPTIONS = [
  { value: 'm2', label: 'm²' },
  { value: 'ml', label: 'ml' },
  { value: 'unid', label: 'Unidad' },
  { value: 'kg', label: 'kg' },
  { value: 'glb', label: 'Global' },
  { value: 'lt', label: 'Litro' },
  { value: 'gal', label: 'Galón' },
  { value: 'rollo', label: 'Rollo' },
  { value: 'funda', label: 'Funda' },
  { value: 'plancha', label: 'Plancha' },
];


function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function toNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// descuentoPct is a percentage (0–100), not a dollar amount
function calculateTotals(items: ItemFormValues[], descuentoPct: number, ivaRate: number): ProformaTotals {
  let subtotalMo = 0;
  let subtotalMat = 0;
  let subtotalConUtilidad = 0;

  for (const item of items) {
    const precioMo = toNum(item.precioMo);
    const precioMat = toNum(item.precioMateriales);
    const cantidad = toNum(item.cantidad);
    const itemMo = precioMo * cantidad;
    const itemMat = precioMat * cantidad;
    // Round precio_unitario to 2dp before multiplying by cantidad so the
    // accumulated summary matches what each row displays (same logic as backend).
    const precioUnitBase = precioMo + precioMat;
    const divisor = 1 - toNum(item.porcentajeUtilidad) / 100;
    const precioUnitario = divisor > 0 ? precioUnitBase / divisor : precioUnitBase;
    const precioUnitario2dp = Math.round(precioUnitario * 100) / 100;
    const itemWithUtility = precioUnitario2dp * cantidad;

    subtotalMo += itemMo;
    subtotalMat += itemMat;
    subtotalConUtilidad += itemWithUtility;
  }

  const subtotalSinUtilidad = subtotalMo + subtotalMat;
  const descuentoAmount = subtotalConUtilidad * descuentoPct / 100;
  const subtotalConDescuento = subtotalConUtilidad - descuentoAmount;
  const iva = subtotalConDescuento * (ivaRate / 100);
  const total = subtotalConDescuento + iva;

  return {
    subtotal_mano_obra: Math.round(subtotalMo * 100) / 100,
    subtotal_materiales: Math.round(subtotalMat * 100) / 100,
    subtotal_sin_utilidad: Math.round(subtotalSinUtilidad * 100) / 100,
    utilidad_valor: Math.round((subtotalConUtilidad - subtotalSinUtilidad) * 100) / 100,
    // Margin on sale price (not markup on cost)
    utilidad_porcentaje: subtotalConUtilidad > 0
      ? Math.round(((subtotalConUtilidad - subtotalSinUtilidad) / subtotalConUtilidad) * 10000) / 100
      : 0,
    subtotal_con_utilidad: Math.round(subtotalConUtilidad * 100) / 100,
    descuento_amount: Math.round(descuentoAmount * 100) / 100,
    subtotal_con_descuento: Math.round(subtotalConDescuento * 100) / 100,
    iva: Math.round(iva * 100) / 100,
    total: Math.round(total * 100) / 100,
  };
}

function dtoToFormItem(item: ProformaItemDTO, index: number): ItemFormValues {
  return {
    _serverId: item.id,
    recipeId: item.recipe_id,
    codigo: item.codigo,
    descripcion: item.descripcion,
    descripcionProyecto: item.descripcion_proyecto,
    categoria: item.categoria as ItemFormValues['categoria'],
    unidad: item.unidad as ItemFormValues['unidad'],
    cantidad: item.cantidad,
    precioMo: item.precio_mo,
    precioMateriales: item.precio_materiales,
    porcentajeUtilidad: item.porcentaje_utilidad,
    aplicacionId: item.aplicacion_id ?? null,
    orden: item.orden || index,
  };
}

/** Find matching medida by rubro + item type — returns quantity and aplicacion_id */
function findMedidaMatch(
  medidas: MedidaDTO[],
  categoria: string,
  descripcion: string,
): { cantidad: number; aplicacionId: string | null } | null {
  const match = medidas.find(
    (m) => m.rubro_tipo === categoria && m.item_tipo.toLowerCase() === descripcion.toLowerCase(),
  );
  return match ? { cantidad: match.cantidad, aplicacionId: match.aplicacion_id } : null;
}

export function ProformaBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useGoBack('/proformas');
  const user = useAuthStore((s) => s.user);
  const canEdit = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const { data: proforma, isLoading, isError, refetch } = useProforma(id!);
  const batchUpsert = useBatchUpsertItems();
  const deleteItem = useDeleteProformaItem();
  const updateProforma = useUpdateProforma();
  const { data: proformaFotos } = useProformaFotos(id);
  const createProformaFoto = useCreateProformaFoto();
  const syncRecipePrices = useSyncRecipePrices();
  const syncMedidas = useSyncMedidasToProforma();

  const { data: aplicacionesData } = useAplicaciones();

  // Load medidas for the project
  const { data: medidasData } = useMedidas(proforma?.project_id);

  // Load medida fotos, medida sets (for residente notes), and project notes
  const { data: medidaFotos } = useMedidaFotos(proforma?.project_id);
  const { data: medidaSets } = useMedidaSets(proforma?.project_id);
  const { data: project } = useProject(proforma?.project_id ?? '');

  // Load client recipe rates for suggestions
  const { data: clientRates } = useClientRecipeRates(proforma?.client_id);

  const [showRecipeModal, setShowRecipeModal] = useState(false);
  const [showCreateRecipe, setShowCreateRecipe] = useState(false);
  const [showMetaModal, setShowMetaModal] = useState(false);
  const [showImpactModal, setShowImpactModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState<FormValues | null>(null);
  const [observacionesLocal, setObservacionesLocal] = useState('');
  const [marcasLocal, setMarcasLocal] = useState('');
  const [descuentoLocal, setDescuentoLocal] = useState('');
  const [ivaLocal, setIvaLocal] = useState('');
  const [fieldsInitialized, setFieldsInitialized] = useState(false);

  // Initialize text fields from proforma (once)
  if (!fieldsInitialized && proforma) {
    setObservacionesLocal(proforma.observaciones ?? '');
    setMarcasLocal((proforma as Record<string, unknown>).marcas as string ?? '');
    // Convert stored dollar-amount discount → percentage of subtotal_con_utilidad
    // NOTE: cannot reference defaultItems here (TDZ — declared later via useMemo)
    const initItems = (proforma.items ?? []).map(dtoToFormItem);
    const baseTotals = calculateTotals(initItems, 0, 100);
    const descuentoPct = baseTotals.subtotal_con_utilidad > 0
      ? Math.round((proforma.descuento / baseTotals.subtotal_con_utilidad) * 10000) / 100
      : 0;
    setDescuentoLocal(String(descuentoPct));
    setIvaLocal(String(proforma.iva_rate));
    setFieldsInitialized(true);
  }

  // Photo upload handler
  // Track registered keys to avoid duplicates
  // (onFilesChange passes the FULL array each time, not just new files)
  const registeredFotoKeys = useRef(new Set<string>());

  const handleFotoUpload = async (files: UploadedFile[]) => {
    if (!id) return;
    for (const file of files) {
      if (file.s3_key && !registeredFotoKeys.current.has(file.s3_key)) {
        registeredFotoKeys.current.add(file.s3_key);
        try {
          await createProformaFoto.mutateAsync({
            proformaId: id,
            data: { s3Key: file.s3_key, filename: file.filename, mimeType: file.mime_type },
          });
        } catch { /* hook handles */ }
      }
    }
  };

  const isBorrador = proforma?.estado === 'borrador';
  const isAprobada = proforma?.estado === 'aprobada';
  const isEnviada = proforma?.estado === 'enviada';
  const isEnviadoAlCliente = proforma?.estado === 'enviado_al_cliente';
  const isGerOp = ['gerente', 'operaciones'].includes(user?.role ?? '');
  // Allow editing in borrador (normal), aprobada (propagation per T-021),
  // enviada when user is gerente or operaciones (adjustments during review),
  // and enviado_al_cliente for any admin role (client-requested adjustments)
  const isEditable = canEdit && (isBorrador || isAprobada || (isEnviada && isGerOp) || isEnviadoAlCliente);
  const hasOT = proforma?.has_ot ?? false;

  // Impact data for approved proformas (T-021)
  const { data: impactData } = useProformaImpact(id!, isAprobada && hasOT);

  // ── Form setup ──
  const defaultItems = useMemo(
    () => (proforma?.items ?? []).map(dtoToFormItem),
    [proforma?.items],
  );

  const { control, register, handleSubmit, getValues, setValue } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { items: defaultItems },
    values: { items: defaultItems },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  const watchedItems = useWatch({ control, name: 'items' });

  // ── Real-time totals (descuentoLocal is a percentage 0-100) ──
  const totals = useMemo(
    () => calculateTotals(watchedItems ?? [], parseFloat(descuentoLocal) || 0, parseFloat(ivaLocal) || 0),
    [watchedItems, descuentoLocal, ivaLocal],
  );

  // ── Add blank row ──
  const handleAddRow = useCallback(() => {
    append({
      _serverId: undefined,
      recipeId: null,
      codigo: '',
      descripcion: '',
      descripcionProyecto: null,
      categoria: 'gypsum',
      unidad: 'm2',
      cantidad: 0,
      precioMo: 0,
      precioMateriales: 0,
      porcentajeUtilidad: 0,
      aplicacionId: null,
      orden: fields.length,
    });
  }, [append, fields.length]);

  // ── Add from recipe (with medida auto-suggest + client rate suggestion) ──
  const handleAddFromRecipe = useCallback(
    (recipe: RecipeListDTO) => {
      const medidaMatch = medidasData
        ? findMedidaMatch(medidasData, recipe.categoria, recipe.nombre)
        : null;

      // Use client agreed rate if available, fall back to recipe base
      const clientRate = clientRates?.get(recipe.id);
      const utilidad = clientRate ?? recipe.porcentaje_utilidad_base;

      append({
        _serverId: undefined,
        recipeId: recipe.id,
        codigo: recipe.codigo,
        descripcion: recipe.nombre,
        descripcionProyecto: null,
        categoria: recipe.categoria as ItemFormValues['categoria'],
        unidad: recipe.unidad as ItemFormValues['unidad'],
        cantidad: medidaMatch?.cantidad ?? 0,
        precioMo: recipe.precio_mo_base,
        precioMateriales: recipe.precio_materiales_base,
        porcentajeUtilidad: utilidad,
        aplicacionId: medidaMatch?.aplicacionId ?? null,
        orden: fields.length,
      });
      setShowRecipeModal(false);

      const msgs: string[] = [];
      if (medidaMatch?.cantidad != null) msgs.push(`Cantidad ${medidaMatch.cantidad} sugerida desde medidas`);
      if (clientRate !== undefined) msgs.push(`Utilidad ${clientRate}% desde historial del cliente`);
      if (msgs.length > 0) toast('success', msgs.join(' · '));
    },
    [append, fields.length, medidasData, clientRates],
  );

  // ── Apply client rates to all existing recipe items ──
  const handleApplyClientRates = useCallback(() => {
    if (!clientRates || clientRates.size === 0) return;
    let applied = 0;
    fields.forEach((_, index) => {
      const recipeId = getValues(`items.${index}.recipeId`);
      if (recipeId && clientRates.has(recipeId)) {
        setValue(`items.${index}.porcentajeUtilidad`, clientRates.get(recipeId)!);
        applied++;
      }
    });
    if (applied > 0) toast('success', `Tasas del cliente aplicadas a ${applied} ítem(s)`);
    else toast('warning', 'No hay tasas guardadas para los ítems actuales de este cliente');
  }, [clientRates, fields, getValues, setValue]);

  // ── Delete row ──
  const handleDeleteRow = useCallback(
    async (index: number) => {
      const serverId = getValues(`items.${index}._serverId`);
      if (serverId && id) {
        await deleteItem.mutateAsync({ proformaId: id, itemId: serverId });
      }
      remove(index);
    },
    [remove, deleteItem, getValues, id],
  );

  // ── Execute save (shared between direct and confirmed saves) ──
  const executeSave = useCallback(async (data: FormValues) => {
    if (!id) return;

    const items = data.items.map((item, i) => ({
      id: item._serverId || undefined,
      recipeId: item.recipeId || undefined,
      codigo: item.codigo,
      descripcion: item.descripcion,
      descripcionProyecto: item.descripcionProyecto || undefined,
      categoria: item.categoria,
      unidad: item.unidad,
      cantidad: item.cantidad,
      precioMo: item.precioMo,
      precioMateriales: item.precioMateriales,
      porcentajeUtilidad: item.porcentajeUtilidad,
      aplicacionId: item.aplicacionId || undefined,
      orden: i,
    }));

    await batchUpsert.mutateAsync({ proformaId: id, data: { items } });
  }, [id, batchUpsert]);

  // ── Save all items (with validations + impact check) ──
  const handleSave = handleSubmit(async (data) => {
    if (!id) return;

    // Validation: warn if 0 items
    if (data.items.length === 0) {
      toast('warning', 'La proforma no tiene ítems. Agrega al menos uno antes de guardar.');
      return;
    }

    // Validation: warn if any item has 0 quantity
    const zeroQtyItems = data.items.filter((item) => item.cantidad === 0);
    if (zeroQtyItems.length > 0) {
      toast('warning', `${zeroQtyItems.length} ítem(s) tienen cantidad 0. Revisa antes de guardar.`);
    }

    // T-021: If editing approved proforma with OT, show impact confirmation
    if (isAprobada && hasOT) {
      setPendingSaveData(data);
      setShowImpactModal(true);
      return;
    }

    await executeSave(data);
  });

  // ── Confirm save after impact dialog ──
  const handleConfirmSave = useCallback(async () => {
    if (!pendingSaveData) return;
    setShowImpactModal(false);
    await executeSave(pendingSaveData);
    setPendingSaveData(null);
  }, [pendingSaveData, executeSave]);

  // ── Loading ──
  if (isLoading) {
    return (
      <div className="space-y-6 fade-up-1">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-brand-dark">Proforma</h1>
        </div>
        <Card><div className="p-6"><SkeletonTable rows={5} cols={6} /></div></Card>
      </div>
    );
  }

  if (isError || !proforma) {
    return (
      <div className="space-y-6 fade-up-1">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-brand-dark">Proforma</h1>
        </div>
        <Card>
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">Error al cargar la proforma</p>
            <Button variant="secondary" className="mt-4" onClick={() => refetch()}>Reintentar</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 fade-up-1">
      {/* OT propagation warning banner */}
      {hasOT && isEditable && (
        <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <AlertTriangle size={16} className="shrink-0" />
          <span>Esta proforma tiene una Orden de Trabajo asociada. Los cambios se propagarán a la OT y pedidos de materiales.</span>
        </div>
      )}

      {/* Review-mode edit banner (enviada + gerente/operaciones) */}
      {isEnviada && isGerOp && (
        <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <AlertTriangle size={16} className="shrink-0" />
          <span>Proforma en revisión. Puedes ajustar los ítems antes de enviar al cliente. El estado se mantiene como <strong>Enviada</strong> hasta que tomes una acción.</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={goBack} className="text-gray-400 hover:text-gray-600">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-brand-dark">
              Proforma #{proforma.numero}
            </h1>
            <p className="text-xs text-gray-400">{proforma.project_codigo}</p>
          </div>
          <StatusBadge entity="proforma" status={proforma.estado} />
        </div>
        <div className="flex items-center gap-2">
          {false && isBorrador && canEdit && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw size={14} />}
              onClick={() => id && syncMedidas.mutate(id)}
              isLoading={syncMedidas.isPending}
              title="Agregar ítems de medidas que aún no están en la proforma"
            >
              Sincronizar medidas
            </Button>
          )}
          {isBorrador && canEdit && (
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<RefreshCw size={14} />}
              onClick={() => id && syncRecipePrices.mutate(id)}
              isLoading={syncRecipePrices.isPending}
              title="Actualiza costos de MO y materiales desde los APUs actuales — preserva las utilidades/precios que hayas ajustado manualmente"
            >
              Actualizar precios
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setShowMetaModal(true)}>
            Detalles
          </Button>
          {canEdit && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Eye size={14} />}
              onClick={() => navigate(`/proformas/${id}/preview`)}
            >
              Vista Previa
            </Button>
          )}
          {isEditable && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Save size={14} />}
              onClick={handleSave}
              isLoading={batchUpsert.isPending}
            >
              Guardar
            </Button>
          )}
        </div>
      </div>

      {/* Zero-price warning banner */}
      {watchedItems && watchedItems.some((item) => item.recipeId && toNum(item.precioMo) === 0 && toNum(item.precioMateriales) === 0) && (
        <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-red-500" />
          <span>
            Hay ítems con precio en cero (marcados en rojo). El APU vinculado no tiene materiales ni mano de obra asignados.
            Ve al módulo de <strong>APUs</strong> para completar los precios, luego usa <strong>"Actualizar precios"</strong> en esta proforma.
          </span>
        </div>
      )}

      {/* Items table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="w-8 px-2 py-3">#</th>
                <th className="px-2 py-3">Código</th>
                <th className="min-w-[200px] px-2 py-3">Descripción</th>
                <th className="px-2 py-3">Und.</th>
                <th className="px-2 py-3 text-right">Cant.</th>
                <th className="px-2 py-3 text-right">P. M.O.</th>
                <th className="px-2 py-3 text-right">P. Mat.</th>
                <th className="px-2 py-3 text-right">Util. %</th>
                <th className="px-2 py-3 text-right">P. Unit.</th>
                <th className="px-2 py-3 text-right">Subtotal</th>
                <th className="px-2 py-3">Categoría</th>
                {isEditable && <th className="w-10 px-2 py-3" />}
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => {
                const item = watchedItems?.[index];
                const itemDivisor = 1 - toNum(item?.porcentajeUtilidad) / 100;
                const precioUnitBase = toNum(item?.precioMo) + toNum(item?.precioMateriales);
                const precioUnitario = itemDivisor > 0 ? precioUnitBase / itemDivisor : precioUnitBase;
                // Subtotal = round2dp(precio_unitario) × cantidad — matches what the user reads
                const precioUnitario2dp = Math.round(precioUnitario * 100) / 100;
                const itemSubtotal = precioUnitario2dp * toNum(item?.cantidad);
                // Auto-generate code for manual items (no recipeId)
                const displayCode = item?.recipeId
                  ? item?.codigo
                  : generateItemCode(item?.categoria ?? 'gypsum', item?.descripcion ?? '');

                // Warn when a recipe-linked item has zero prices (recipe has no materials/MO yet)
                const hasPriceWarning = !!item?.recipeId
                  && toNum(item?.precioMo) === 0
                  && toNum(item?.precioMateriales) === 0;

                return (
                  <tr key={field.id} className={`border-b hover:bg-gray-50/30 ${hasPriceWarning ? 'border-red-200 bg-red-50/40' : 'border-gray-50'}`}>
                    <td className="px-2 py-2 text-xs text-gray-400">{index + 1}</td>
                    <td className="px-2 py-2">
                      {isEditable && item?.recipeId ? (
                        <input
                          {...register(`items.${index}.codigo`)}
                          className="w-20 rounded border border-gray-200 bg-white px-2 py-1 text-xs focus:border-brand-blue focus:outline-none"
                        />
                      ) : (
                        <span className="font-mono text-xs text-gray-400">{displayCode || '—'}</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {isEditable ? (
                        <input
                          {...register(`items.${index}.descripcion`)}
                          className="w-full min-w-[180px] rounded border border-gray-200 bg-white px-2 py-1 text-xs focus:border-brand-blue focus:outline-none"
                        />
                      ) : (
                        <span className="text-xs">{item?.descripcion}</span>
                      )}
                    </td>
                    <td className="px-2 py-2">
                      {isEditable ? (
                        <select
                          {...register(`items.${index}.unidad`)}
                          className="rounded border border-gray-200 bg-white px-1 py-1 text-xs focus:border-brand-blue focus:outline-none"
                        >
                          {UNIDAD_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs">{item?.unidad}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {isEditable ? (
                        <input
                          type="number"
                          step="0.01"
                          {...register(`items.${index}.cantidad`, { valueAsNumber: true })}
                          className="w-20 rounded border border-gray-200 bg-white px-2 py-1 text-right text-xs focus:border-brand-blue focus:outline-none"
                        />
                      ) : (
                        <span className="text-xs">{item?.cantidad}</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {isEditable ? (
                        <input
                          type="number"
                          step="0.01"
                          {...register(`items.${index}.precioMo`, { valueAsNumber: true })}
                          className={`w-20 rounded border px-2 py-1 text-right text-xs focus:outline-none ${hasPriceWarning ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500' : 'border-gray-200 bg-white focus:border-brand-blue'}`}
                          title={hasPriceWarning ? 'APU sin precios — asigna materiales/MO en el módulo de APUs' : undefined}
                        />
                      ) : (
                        <span className={`text-xs ${hasPriceWarning ? 'font-semibold text-red-600' : ''}`}>
                          {formatCurrency(item?.precioMo ?? 0)}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {isEditable ? (
                        <input
                          type="number"
                          step="0.01"
                          {...register(`items.${index}.precioMateriales`, { valueAsNumber: true })}
                          className={`w-20 rounded border px-2 py-1 text-right text-xs focus:outline-none ${hasPriceWarning ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500' : 'border-gray-200 bg-white focus:border-brand-blue'}`}
                          title={hasPriceWarning ? 'APU sin precios — asigna materiales/MO en el módulo de APUs' : undefined}
                        />
                      ) : (
                        <span className={`text-xs ${hasPriceWarning ? 'font-semibold text-red-600' : ''}`}>
                          {formatCurrency(item?.precioMateriales ?? 0)}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {isEditable ? (
                        <div className="flex flex-col items-end gap-0.5">
                          <input
                            type="number"
                            step="0.01"
                            {...register(`items.${index}.porcentajeUtilidad`, { valueAsNumber: true })}
                            className="w-16 rounded border border-gray-200 bg-white px-2 py-1 text-right text-xs focus:border-brand-blue focus:outline-none"
                          />
                          {item?.recipeId && clientRates?.has(item.recipeId) && (
                            <span
                              className="text-[10px] text-brand-blue cursor-pointer hover:underline"
                              title="Click para aplicar tasa del cliente"
                              onClick={() => setValue(`items.${index}.porcentajeUtilidad`, clientRates.get(item.recipeId!)!)}
                            >
                              cliente: {clientRates.get(item.recipeId)}%
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs">{Number(item?.porcentajeUtilidad).toFixed(2)}%</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-600">
                      {isEditable ? (
                        <input
                          type="number"
                          step="0.01"
                          key={`pu-${field.id}-${precioUnitario.toFixed(4)}`}
                          defaultValue={precioUnitario.toFixed(2)}
                          onBlur={(e) => {
                            const newPu = parseFloat(e.target.value);
                            const displayedPu = Math.round(precioUnitario * 100) / 100;
                            if (!isNaN(newPu) && Math.abs(newPu - displayedPu) > 0.001) {
                              const newPct = calcPorcentajeFromPrecioUnitario(
                                newPu,
                                toNum(item?.precioMo),
                                toNum(item?.precioMateriales),
                              );
                              setValue(`items.${index}.porcentajeUtilidad`, Math.round(newPct * 10000) / 10000);
                            }
                          }}
                          className="w-24 rounded border border-gray-200 bg-white px-2 py-1 text-right text-xs focus:border-brand-blue focus:outline-none"
                          title="Precio unitario de venta — modifica la utilidad automáticamente"
                        />
                      ) : (
                        formatCurrency(precioUnitario)
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-medium text-gray-800">
                      {formatCurrency(itemSubtotal)}
                    </td>
                    <td className="px-2 py-2">
                      {isEditable ? (
                        <select
                          {...register(`items.${index}.aplicacionId`)}
                          className="rounded border border-gray-200 bg-white px-1 py-1 text-xs focus:border-brand-blue focus:outline-none"
                        >
                          <option value="">—</option>
                          {(aplicacionesData ?? []).map((a) => (
                            <option key={a.id} value={a.id}>{a.nombre}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-xs text-gray-500">
                          {(aplicacionesData ?? []).find((a) => a.id === item?.aplicacionId)?.nombre ?? '—'}
                        </span>
                      )}
                    </td>
                    {isEditable && (
                      <td className="px-2 py-2">
                        <button
                          onClick={() => handleDeleteRow(index)}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500"
                          type="button"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add row buttons */}
        {isEditable && (
          <div className="flex flex-wrap gap-2 border-t border-gray-100 px-4 py-3">
            <Button variant="ghost" size="sm" leftIcon={<BookOpen size={14} />} onClick={() => setShowRecipeModal(true)}>
              Desde APU
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => setShowCreateRecipe(true)}
            >
              Crear nuevo APU
            </Button>
            {clientRates && clientRates.size > 0 && fields.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto text-brand-blue border border-brand-blue/30 hover:bg-brand-blue/5"
                onClick={handleApplyClientRates}
                title="Pre-rellena utilidades con los valores acordados para este cliente en proformas anteriores"
              >
                Aplicar tasas del cliente
              </Button>
            )}
          </div>
        )}

        {fields.length === 0 && (
          <div className="px-6 py-8 text-center">
            <p className="text-sm text-gray-400">No hay ítems en esta proforma.</p>
          </div>
        )}
      </Card>

      {/* Summary card */}
      <Card>
        <div className="p-4">
          <h3 className="mb-3 text-center text-sm font-semibold text-gray-700">Resumen</h3>
          <div className="mx-auto max-w-sm space-y-1 text-sm">
            <SummaryRow label="Subtotal Mano de Obra" value={totals.subtotal_mano_obra} />
            <SummaryRow label="Subtotal Materiales" value={totals.subtotal_materiales} />
            <SummaryRow label="Subtotal sin utilidad" value={totals.subtotal_sin_utilidad} muted />
            <SummaryRow label={`Utilidad (${totals.utilidad_porcentaje.toFixed(1)}%)`} value={totals.utilidad_valor} highlight />
            <SummaryRow label="Subtotal con utilidad" value={totals.subtotal_con_utilidad} />
            <div className="flex items-center justify-between text-gray-600">
              <span>Descuento (%)</span>
              {isEditable ? (
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={descuentoLocal}
                    onChange={(e) => setDescuentoLocal(e.target.value)}
                    onBlur={() => {
                      const pct = parseFloat(descuentoLocal) || 0;
                      // Compute base subtotal (no discount) then apply %
                      const baseTotals = calculateTotals(watchedItems ?? [], 0, 100);
                      const descuentoAmount = Math.round(baseTotals.subtotal_con_utilidad * pct / 100 * 100) / 100;
                      if (descuentoAmount !== proforma.descuento) {
                        updateProforma.mutate({ id: id!, data: { descuento: descuentoAmount } });
                      }
                    }}
                    className="w-20 rounded border border-gray-200 px-2 py-0.5 text-right text-sm font-medium focus:border-brand-blue focus:outline-none"
                  />
                  <span className="text-sm text-gray-500">%</span>
                </div>
              ) : (
                <span className="font-medium">
                  {totals.descuento_amount > 0
                    ? `- ${formatCurrency(totals.descuento_amount)}`
                    : formatCurrency(0)}
                </span>
              )}
            </div>
            <SummaryRow label="Subtotal con descuento" value={totals.subtotal_con_descuento} />
            {(isEditable || (parseFloat(ivaLocal) || 0) > 0) && (
              <div className="flex items-center justify-between text-gray-600">
                <span>IVA (%)</span>
                {isEditable ? (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={ivaLocal}
                      onChange={(e) => setIvaLocal(e.target.value)}
                      onBlur={() => {
                        const rate = parseFloat(ivaLocal);
                        if (!isNaN(rate) && rate >= 0 && rate <= 100 && rate !== proforma.iva_rate) {
                          updateProforma.mutate({ id: id!, data: { ivaRate: rate } });
                        }
                      }}
                      className="w-20 rounded border border-gray-200 px-2 py-0.5 text-right text-sm font-medium focus:border-brand-blue focus:outline-none"
                    />
                    <span className="text-sm text-gray-500">%</span>
                  </div>
                ) : (
                  <span className="font-medium">{formatCurrency(totals.iva)}</span>
                )}
              </div>
            )}
            <div className="border-t border-gray-200 pt-2">
              <div className="flex items-center justify-between font-bold text-gray-900">
                <span>Total</span>
                <span>{formatCurrency(totals.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Notas del Residente */}
      {(project?.medidas_notas || medidasData?.some((m) => m.notas) || medidaSets?.some((s) => s.descripcion)) && (
        <Card>
          <div className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <StickyNote size={16} className="text-amber-500" />
              Notas del Residente
            </h3>
            {project?.medidas_notas && (
              <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                <p className="text-xs text-amber-700 whitespace-pre-wrap">{project.medidas_notas}</p>
              </div>
            )}
            {medidaSets?.filter((s) => s.descripcion).map((s) => (
              <div key={s.id} className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                <p className="text-xs font-semibold text-amber-800 mb-1">{s.nombre}</p>
                <p className="text-xs text-amber-700 whitespace-pre-wrap">{s.descripcion}</p>
              </div>
            ))}
            {medidasData?.filter((m) => m.notas).map((m) => (
              <div key={m.id} className="flex items-start gap-2 mb-2 last:mb-0">
                <span className="text-xs font-medium text-gray-600 shrink-0">{m.item_tipo}:</span>
                <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5">{m.notas}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Observaciones */}
      <Card>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <StickyNote size={16} className="text-gray-400" />
            Observaciones
          </h3>
          {isEditable ? (
            <textarea
              value={observacionesLocal}
              onChange={(e) => setObservacionesLocal(e.target.value)}
              onBlur={() => {
                if (observacionesLocal !== (proforma.observaciones ?? '')) {
                  updateProforma.mutate({ id: id!, data: { observaciones: observacionesLocal || null } });
                }
              }}
              placeholder="Notas, observaciones, detalles adicionales..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30 min-h-[80px] resize-y"
              maxLength={5000}
            />
          ) : proforma.observaciones ? (
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{proforma.observaciones}</p>
          ) : (
            <p className="text-sm text-gray-400 italic">Sin observaciones</p>
          )}
        </div>
      </Card>

      {/* Marcas */}
      <Card>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <StickyNote size={16} className="text-gray-400" />
            Marcas
          </h3>
          {isEditable ? (
            <textarea
              value={marcasLocal}
              onChange={(e) => setMarcasLocal(e.target.value)}
              onBlur={() => {
                if (marcasLocal !== ((proforma as Record<string, unknown>).marcas as string ?? '')) {
                  updateProforma.mutate({ id: id!, data: { marcas: marcasLocal || null } });
                }
              }}
              placeholder="Marcas de materiales a utilizar..."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30 min-h-[80px] resize-y"
              maxLength={2000}
            />
          ) : (proforma as Record<string, unknown>).marcas ? (
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{(proforma as Record<string, unknown>).marcas as string}</p>
          ) : (
            <p className="text-sm text-gray-400 italic">Sin marcas especificadas</p>
          )}
        </div>
      </Card>

      {/* Fotos */}
      <Card>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <ImageIcon size={16} className="text-gray-400" />
            Fotos
          </h3>
          {isEditable && (
            <FileUpload
              context="proforma_foto"
              onFilesChange={handleFotoUpload}
              maxFiles={20}
            />
          )}
          {proformaFotos && proformaFotos.length > 0 && (
            <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {proformaFotos.map((foto) => (
                <FotoThumbnail key={foto.id} s3Key={foto.s3_key} filename={foto.filename} />
              ))}
            </div>
          )}
          {medidaFotos && medidaFotos.length > 0 && (
            <div className="mt-3">
              <p className="text-xs text-gray-400 mb-2">Fotos del residente ({medidaFotos.length})</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                {medidaFotos.map((foto) => (
                  <FotoThumbnail key={foto.id} s3Key={foto.s3_key} filename={foto.filename} mimeType={foto.mime_type} />
                ))}
              </div>
            </div>
          )}
          {(!proformaFotos || proformaFotos.length === 0) && (!medidaFotos || medidaFotos.length === 0) && !isEditable && (
            <p className="text-sm text-gray-400 py-4 text-center">No hay fotos</p>
          )}
        </div>
      </Card>

      {/* Recipe picker modal */}
      <RecipePickerModal
        isOpen={showRecipeModal}
        onClose={() => setShowRecipeModal(false)}
        onSelect={handleAddFromRecipe}
        medidas={medidasData ?? []}
      />

      {/* Metadata modal */}
      <MetadataModal
        isOpen={showMetaModal}
        onClose={() => setShowMetaModal(false)}
        proformaId={id!}
        currentData={{
          observaciones: proforma.observaciones,
          formaPago: proforma.forma_pago,
          plazo: proforma.plazo,
          tiempoEntrega: proforma.tiempo_entrega,
          descuento: proforma.descuento,
          ivaRate: proforma.iva_rate,
        }}
        isEditable={isEditable}
        subtotalConUtilidad={totals.subtotal_con_utilidad}
      />

      {/* T-021: Impact confirmation dialog */}
      <ImpactConfirmationModal
        isOpen={showImpactModal}
        onClose={() => { setShowImpactModal(false); setPendingSaveData(null); }}
        onConfirm={handleConfirmSave}
        impact={impactData ?? null}
        isLoading={batchUpsert.isPending}
      />

      {/* Create Recipe Modal (inline, no navigation) */}
      <CreateRecipeModal
        isOpen={showCreateRecipe}
        onClose={() => setShowCreateRecipe(false)}
        onCreated={(recipe) => {
          // Auto-add the newly created recipe to the proforma items
          append({
            _serverId: undefined,
            recipeId: recipe.id,
            codigo: recipe.codigo,
            descripcion: recipe.nombre,
            descripcionProyecto: null,
            categoria: recipe.categoria as 'gypsum' | 'pintura' | 'impermeabilizacion' | 'empaste' | 'general',
            unidad: recipe.unidad as 'm2' | 'ml' | 'unid' | 'kg' | 'glb',
            cantidad: 0,
            precioMo: recipe.precio_mo_base,
            precioMateriales: recipe.precio_materiales_base,
            porcentajeUtilidad: recipe.porcentaje_utilidad_base,
            aplicacionId: null,
            orden: fields.length,
          });
        }}
      />
    </div>
  );
}


function SummaryRow({ label, value, muted, highlight }: { label: string; value: number; muted?: boolean; highlight?: boolean }) {
  if (highlight) {
    return (
      <div className="flex items-center justify-between rounded-md bg-blue-50 px-2 py-1 font-semibold text-blue-700">
        <span>{label}</span>
        <span>{formatCurrency(value)}</span>
      </div>
    );
  }
  return (
    <div className={`flex items-center justify-between ${muted ? 'text-gray-400' : 'text-gray-600'}`}>
      <span>{label}</span>
      <span className="font-medium">{formatCurrency(value)}</span>
    </div>
  );
}

function RecipePickerModal({
  isOpen,
  onClose,
  onSelect,
  medidas,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (recipe: RecipeListDTO) => void;
  medidas: MedidaDTO[];
}) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useRecipes({ search: search || undefined, limit: 50 });

  return (
    <Modal isOpen={isOpen} title="Seleccionar APU" onClose={onClose} size="lg">
      <div className="space-y-4">
        <div className="relative">
          <input
            type="text"
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            placeholder="Buscar APU por código o nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-400">Cargando APUs...</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {data?.data.map((recipe) => {
              const medidaMatch = findMedidaMatch(medidas, recipe.categoria, recipe.nombre);
              return (
                <button
                  key={recipe.id}
                  className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm hover:bg-gray-50"
                  onClick={() => onSelect(recipe)}
                >
                  <div>
                    <p className="font-medium text-gray-800">{recipe.codigo} — {recipe.nombre}</p>
                    <p className="text-xs text-gray-400">
                      MO: ${recipe.precio_mo_base.toFixed(2)} | Mat: ${recipe.precio_materiales_base.toFixed(2)} | Util: {recipe.porcentaje_utilidad_base}%
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-gray-400">{recipe.categoria}</span>
                    {medidaMatch !== null && (
                      <p className="text-xs font-medium text-brand-blue">Medida: {medidaMatch.cantidad}</p>
                    )}
                  </div>
                </button>
              );
            })}
            {data?.data.length === 0 && (
              <p className="py-4 text-center text-sm text-gray-400">No se encontraron APUs</p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function MetadataModal({
  isOpen,
  onClose,
  proformaId,
  currentData,
  isEditable,
  subtotalConUtilidad,
}: {
  isOpen: boolean;
  onClose: () => void;
  proformaId: string;
  currentData: {
    observaciones: string | null;
    formaPago: string | null;
    plazo: string | null;
    tiempoEntrega: string | null;
    descuento: number;
    ivaRate: number;
  };
  isEditable: boolean;
  subtotalConUtilidad: number;
}) {
  const updateMutation = useUpdateProforma();

  // Convert stored dollar amount to percentage for display
  const descuentoPctDefault = subtotalConUtilidad > 0
    ? Math.round((currentData.descuento / subtotalConUtilidad) * 10000) / 100
    : 0;

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: zodResolver(updateProformaSchema),
    defaultValues: {
      observaciones: currentData.observaciones ?? '',
      formaPago: currentData.formaPago ?? '',
      plazo: currentData.plazo ?? '',
      tiempoEntrega: currentData.tiempoEntrega ?? '',
      descuento: descuentoPctDefault,
      ivaRate: currentData.ivaRate,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    // Convert percentage back to dollar amount before saving
    const descuentoAmount = Math.round(subtotalConUtilidad * (data.descuento ?? 0) / 100 * 100) / 100;
    await updateMutation.mutateAsync({ id: proformaId, data: { ...data, descuento: descuentoAmount } });
    onClose();
  });

  return (
    <Modal isOpen={isOpen} title="Detalles de Proforma" onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Observaciones"
          {...register('observaciones')}
          error={errors.observaciones?.message}
          disabled={!isEditable}
        />
        <Input
          label="Forma de Pago"
          {...register('formaPago')}
          error={errors.formaPago?.message}
          disabled={!isEditable}
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Plazo"
            {...register('plazo')}
            error={errors.plazo?.message}
            disabled={!isEditable}
          />
          <Input
            label="Tiempo de Entrega"
            {...register('tiempoEntrega')}
            error={errors.tiempoEntrega?.message}
            disabled={!isEditable}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Descuento (%)"
            type="number"
            step="0.01"
            min="0"
            max="100"
            {...register('descuento', { valueAsNumber: true })}
            error={errors.descuento?.message}
            disabled={!isEditable}
          />
          <Input
            label="IVA (%)"
            type="number"
            step="0.01"
            {...register('ivaRate', { valueAsNumber: true })}
            error={errors.ivaRate?.message}
            disabled={!isEditable}
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cerrar</Button>
          {isEditable && (
            <Button variant="primary" type="submit" isLoading={updateMutation.isPending}>
              Guardar cambios
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}

// ── T-021: Impact confirmation modal ──
function ImpactConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  impact,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  impact: ProformaImpactDTO | null;
  isLoading: boolean;
}) {
  return (
    <Modal isOpen={isOpen} title="Confirmar cambios — Propagación" onClose={onClose} size="md">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Esta proforma está aprobada y tiene módulos asociados.
          Los cambios se propagarán a los siguientes módulos:
        </p>

        {impact && (
          <>
            {/* Affected modules */}
            {impact.affected_modules.length > 0 && (
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <h4 className="text-sm font-semibold text-blue-800 mb-1">Módulos afectados:</h4>
                <ul className="space-y-1">
                  {impact.affected_modules.map((mod) => (
                    <li key={mod} className="text-sm text-blue-700 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      {mod}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Warnings */}
            {impact.warnings.length > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3">
                <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                  <AlertTriangle size={14} className="inline mr-1 -mt-0.5" />
                  Advertencias:
                </h4>
                <ul className="space-y-1">
                  {impact.warnings.map((w, i) => (
                    <li key={i} className="text-sm text-yellow-700">{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Summary counts */}
            <div className="text-xs text-gray-500 space-y-0.5">
              {impact.has_planillas && (
                <p>{impact.planillas_count} planilla(s) en el proyecto</p>
              )}
              {impact.has_pedidos && (
                <p>{impact.pedidos_count} pedido(s) de materiales</p>
              )}
            </div>
          </>
        )}

        <p className="text-sm font-medium text-gray-700">
          ¿Deseas confirmar los cambios?
        </p>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button variant="primary" onClick={onConfirm} isLoading={isLoading}>
            Confirmar y guardar
          </Button>
        </div>
      </div>
    </Modal>
  );
}
